import request from "@/utils/request";
import { notify } from "@kyvg/vue3-notification";

function getPdfBlob(data) {
  if (!data) return null;

  if (data instanceof Blob) {
    return data;
  }

  if (data instanceof ArrayBuffer) {
    return new Blob([data], { type: "application/pdf" });
  }

  if (Array.isArray(data) && data.length > 0 && typeof data[0] === "number") {
    const bytes = new Uint8Array(data);
    return new Blob([bytes], { type: "application/pdf" });
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("blob:")
    ) {
      return trimmed;
    }

    try {
      const cleanBase64 = trimmed
        .replace(/^data:[^;]+;base64,/, "")
        .replace(/\s/g, "");
      const binaryString = window.atob(cleanBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], { type: "application/pdf" });
    } catch (e) {
      console.error("Base64 decoding failed:", e);
      return null;
    }
  }

  return null;
}

export function apiExecuteDataSet(param) {
  // console.log("apiExecuteDataSet param", param);

  return request({
    url: "/api/ExecuteDataSet",
    method: "post",
    data: {
      SYSTEM: 0,
      UseBytes: false,
      ...param,
    },
    timeout: 60000,
    //useProgress: false,
  });
}

//SP 실행(NonQuery)
export function apiExecuteNonQuery(param) {
  // console.log("apiExecuteDataSet param", param);

  return request({
    url: "/api/ExecuteNonQuery",
    method: "post",
    data: {
      SYSTEM: 0,
      UseBytes: false,
      ...param,
    },
    timeout: 60000,
    //useProgress: false,
  });
}

// 리포트 팝업처리
export async function apiExecuteReport(param, exportParams = {}) {
  const {
    title = "보고서",
    name = "",
    width = 1100,
    height = 900,
    target = "_blank",
    autoPopup = true,
  } = exportParams;

  const popupTitle = title || name || "보고서";
  const left = Math.max(0, (window.screen.width - width) / 2);
  const top = Math.max(0, (window.screen.height - height) / 2);
  const windowFeatures = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;

  let popup = null;
  if (autoPopup && typeof window !== "undefined") {
    try {
      popup = window.open("", target, windowFeatures);
      if (popup) {
        popup.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>${popupTitle}</title>
            <style>
              body {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                background-color: #f8fafc;
                color: #334155;
              }
              .container {
                text-align: center;
              }
              .spinner {
                width: 48px;
                height: 48px;
                margin: 0 auto 16px;
                border: 4px solid #e2e8f0;
                border-top: 4px solid #3b82f6;
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              .title {
                font-size: 18px;
                font-weight: 600;
                margin-bottom: 6px;
              }
              .desc {
                font-size: 14px;
                color: #64748b;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="spinner"></div>
              <div class="title">${popupTitle}를 불러오는 중입니다...</div>
              <div class="desc">잠시만 기다려 주세요.</div>
            </div>
          </body>
          </html>
        `);
        popup.document.close();
      }
    } catch (e) {
      console.warn("Popup pre-open failed:", e);
    }
  }

  try {
    const res = await request({
      url: "/api/Report",
      method: "post",
      data: param,
      timeout: 60000,
      responseType: "blob",
    });

    let resultData = res;

    // 만약 res가 여전히 Blob이지만 내용이 JSON인 경우 텍스트 파싱
    if (resultData instanceof Blob) {
      try {
        const text = await resultData.text();
        const trimmed = text.trim();
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
          const json = JSON.parse(trimmed);
          if (
            json &&
            (json.Message !== undefined ||
              json.Code !== undefined ||
              json._Result !== undefined)
          ) {
            resultData = json;
          }
        }
      } catch (e) {
        // 일반 binary blob
      }
    }

    // JSON 응답인 경우 (Message가 있거나 에러 코드가 있는 경우 처리)
    if (
      resultData &&
      typeof resultData === "object" &&
      !(resultData instanceof Blob) &&
      !(resultData instanceof ArrayBuffer)
    ) {
      const code = resultData.Code !== undefined ? resultData.Code : null;
      const message =
        resultData.Message || resultData.msg || resultData.error || "";
      const isError =
        (code !== null && code !== 200 && code !== 0) ||
        (resultData._Result === null && message) ||
        Boolean(message);

      if (isError) {
        if (popup && !popup.closed) popup.close();
        notify({
          title: "알림",
          text: message || "보고서 조회에 실패했습니다.",
          type: "warn",
        });
        return resultData;
      }
    }

    if (autoPopup) {
      const rawData =
        resultData._Result ||
        resultData.DataSetBytes ||
        resultData.body ||
        resultData.data ||
        resultData;
      const pdfBlobOrUrl = getPdfBlob(rawData);

      if (!pdfBlobOrUrl) {
        if (popup && !popup.closed) popup.close();
        notify({
          title: "알림",
          text: "보고서 데이터가 없거나 형식이 올바르지 않습니다.",
          type: "warn",
        });
        return resultData;
      }

      const finalUrl =
        typeof pdfBlobOrUrl === "string"
          ? pdfBlobOrUrl
          : URL.createObjectURL(pdfBlobOrUrl);

      if (popup && !popup.closed) {
        popup.location.href = finalUrl;
      } else {
        window.open(finalUrl, target, windowFeatures);
      }
    }

    return resultData;
  } catch (err) {
    if (popup && !popup.closed) popup.close();
    console.error("apiExecuteReport error:", err);

    let errorMsg = "보고서를 가져오는 도중 오류가 발생했습니다.";
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        if (json?.Message) errorMsg = json.Message;
      } catch (e) {
        console.warn("에러 Blob JSON 파싱 실패:", e);
      }
    } else if (err?.response?.data?.Message) {
      errorMsg = err.response.data.Message;
    } else if (err?.message) {
      errorMsg = err.message;
    }

    notify({
      title: "알림",
      text: errorMsg,
      type: "warn",
    });
    return null;
  }
}
